import Point from "@arcgis/core/geometry/Point";
import Graphic from "@arcgis/core/Graphic";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import { AppConfig } from "../AppConfig";
import { drawSectors } from "../components/MapTools/LightHouse/lighthouseDrawing";
import {
    applyEdits,
    computeTrueNorth,
    createRelatedFeatures,
    findAndSelectFeature,
    getAllRelatedFeatures,
    getMapView,
    removedAndDiscontinuedDefExpr,
    setFeatureAttribute,
} from "./arcgisUtils";
import { metersPerNauticalMile } from "./constants";
import { clamp, clampWithCircleRollover, deepCopy, uuidv4 } from "./helpers";
import { buildLighthouseData } from "./lighthouseCreation";
import { DefaultSectorValues, LighthouseData, Sector, SectorColor } from "./lighthouseConstants";
import { NFSStatus, setNewStatusToFeature } from "./nfsUtils";
import { toast } from "react-toastify";
import { getKystverketRegion, getKystverketRode } from "./autofillUtils";
import { NauticalClasses } from "../constants/NauticalObjects";
import { readSectorsForFeature } from "./sectorRead";
import { getFirstDarkSectorIndex, hasSameColorConsecutiveSectors, satisfiesIALA, shiftSectors } from "./sectorRules";

// Re-exported so existing call sites (e.g. SectorEditorContent.tsx) keep importing these from
// lighthouseUtils; the logic itself now lives in sectorRules.ts, shared with sectorDraftValidation.ts.
export { getFirstDarkSectorIndex, hasSameColorConsecutiveSectors, satisfiesIALA, shiftSectors };

export const deleteSector = (lighthouseData: LighthouseData, sectorIndex: number) => {
    const newLighthouseData = deepCopy(lighthouseData);
    newLighthouseData.sectors.splice(sectorIndex, 1);

    // Update sector.index for all sectors after the deleted sector
    for (const [index, sector] of newLighthouseData.sectors.entries()) {
        sector.index = index;
    }

    return newLighthouseData;
};

export const createSector = (lighthouseData: LighthouseData, sectorIndex: number) => {
    const newLighthouseData = { ...lighthouseData };
    const currentSector = newLighthouseData.sectors[sectorIndex];
    const nextSector =
        newLighthouseData.sectors[(sectorIndex + 1) % newLighthouseData.sectors.length] ?? newLighthouseData.sectors[0];

    const startAngle = currentSector.startAngle ?? 0;
    let endAngle = nextSector.startAngle ?? 360;

    if (endAngle < startAngle) {
        endAngle += 360;
    }

    newLighthouseData.sectors.splice(sectorIndex + 1, 0, {
        globalid: uuidv4(),
        lighthouseId: lighthouseData.id,
        color: SectorColor.WHITE,
        startAngle:
            lighthouseData.sectors.length === 1 ? (startAngle + 180) % 360 : (endAngle - startAngle) / 2 + startAngle,
        innerRadius: DefaultSectorValues.defaultInnerRadius,
        startLineLength: DefaultSectorValues.defaultLineLength,
        endLineLength: DefaultSectorValues.defaultLineLength,
        index: nextSector.index,
        description: "",
        nfs_status: NFSStatus.PROJECTED,
        nfs_status_line1: NFSStatus.PROJECTED,
        nfs_status_line2: NFSStatus.PROJECTED,
    });

    // Sort sectors by angle
    newLighthouseData.sectors.sort((a, b) => {
        return a.startAngle! - b.startAngle!;
    });

    for (const [index, sector] of newLighthouseData.sectors.entries()) {
        sector.index = index;
    }

    return newLighthouseData;
};

export const sanitizeSectors = (data: LighthouseData, currentIndex: number) => {
    const prevIndex = currentIndex === 0 ? data.sectors.length - 1 : currentIndex - 1;
    const nextIndex = currentIndex === data.sectors.length - 1 ? 0 : currentIndex + 1;
    const prevSector = data.sectors[prevIndex];
    const currentSector = data.sectors[currentIndex];
    const nextSector = data.sectors[nextIndex];

    const currentRadius = currentSector?.innerRadius ?? DefaultSectorValues.defaultInnerRadius;
    const startLineLength = currentSector.startLineLength ?? DefaultSectorValues.defaultLineLength;

    // Limit the sector line lengths to absolute max and min values
    currentSector.startLineLength = clamp(
        startLineLength,
        AppConfig.Lighthouse.MinSectorLineLength,
        AppConfig.Lighthouse.MaxSectorLineLength
    );

    // Limit the sector radius
    currentSector.innerRadius = clamp(currentRadius, 0);

    let prevAngle = prevSector.startAngle ?? 0;
    let currentAngle = currentSector.startAngle ?? 0;
    let nextAngle = nextSector.startAngle ?? 360;

    // Constrain all angles to be between 0 and 360
    currentAngle = (currentAngle + 360) % 360;
    prevAngle = (prevAngle + 360) % 360;
    nextAngle = (nextAngle + 360) % 360;

    if (data.sectors.length <= 2) {
        currentSector.startAngle = clampWithCircleRollover(currentAngle, prevAngle - 360, nextAngle);
    }
    // Clamp the current sector angle to be between the previous and next sector angles.
    // Also handle special cases for crossing the 0/360 degree line.
    else if (currentSector.index === 0 || currentSector.index === data.sectors.length - 1) {
        currentSector.startAngle = clampWithCircleRollover(currentAngle, prevAngle, nextAngle);
    } else if (currentSector.index === 1 && currentIndex === 0) {
        const prevPrevAngle = data.sectors[(prevIndex - 1) % data.sectors.length].startAngle ?? 0;
        prevSector.startAngle = clampWithCircleRollover(prevAngle, prevPrevAngle, currentAngle);
    } else if (currentIndex === data.sectors.length - 1 && currentSector.index !== data.sectors.length - 1) {
        const nextNextAngle = data.sectors[(nextIndex + 1) % data.sectors.length].startAngle ?? 0;
        nextSector.startAngle = clampWithCircleRollover(nextAngle, currentAngle, nextNextAngle);
    } else {
        currentSector.startAngle = clamp(currentAngle, prevAngle, nextAngle);
    }

    // Sort sectors by angle
    data.sectors.sort((a, b) => {
        return a.startAngle! - b.startAngle!;
    });

    return data;
};

export const fillLighthouseAttributes = (newFeature: Graphic) => {
    const mapview = getMapView();
    if (!mapview?.map) return;

    const lysLayer = mapview.map.findLayerById("Lys") as FeatureLayer;
    const lysAttributes = lysLayer.fields
        .map((field) => field.name)
        .reduce((acc, field) => ({ ...acc, [field]: null }), {});
    newFeature.attributes = { ...lysAttributes, ...newFeature.attributes };

    const fyrlyktAttributesConfig = AppConfig.CreateObject.find(
        (config) => config.GroupName === "Lys"
    )?.GroupEntries.find((entry) => entry.Title === "Fyrlykt");

    if (fyrlyktAttributesConfig) {
        for (const attribute of fyrlyktAttributesConfig.FieldValues) {
            newFeature.attributes[attribute.FieldName] = attribute.Value;
        }
    }
    newFeature.layer = lysLayer;
};

export const getRelationshipId = (layer: FeatureLayer) => {
    const mapView = getMapView();
    if (!mapView?.map) return;

    const sectorTable = mapView.map.findTableById("Lyssektor") as FeatureLayer;
    const layerTitleAndRelationshipName = {
        Sektorflate: "Lyssektor_F",
        Sektorlinje: "Lyssektor_L",
        Lys: "Lys",
    };

    return sectorTable.relationships?.find(
        (relationship) =>
            relationship.name ===
            layerTitleAndRelationshipName[
                (layer?.title ?? layer?.id ?? "") as keyof typeof layerTitleAndRelationshipName
            ]
    )?.id;
};

const createSingleLightSector = (feature: Graphic | null | undefined) => {
    if (!feature) return [] as Sector[];

    const sector = {
        globalid: uuidv4(),
        lighthouseId: "",
        color: feature.attributes.lys_farge.toString() ?? SectorColor.WHITE,
        startAngle: 120,
        endAngle: 150,
        innerRadius: 100,
        startLineLength: 200,
        index: 0,
        description: "",
        nfs_status: feature.attributes.nfs_status ?? NFSStatus.PROJECTED,
        nfs_status_line1: feature.attributes.nfs_status_line1 ?? NFSStatus.PROJECTED,
        nfs_status_line2: feature.attributes.nfs_status_line2 ?? NFSStatus.PROJECTED,
    };
    const darkSector = {
        globalid: uuidv4(),
        lighthouseId: "",
        color: SectorColor.DARK,
        startAngle: 150,
        endAngle: 120,
        innerRadius: 100,
        startLineLength: 200,
        index: 1,
        description: "",
        nfs_status: NFSStatus.UNDEFINED,
        nfs_status_line1: NFSStatus.PROJECTED,
        nfs_status_line2: NFSStatus.PROJECTED,
    };

    return [sector, darkSector];
};

export const loadLighthouse = async (
    objectid: number,
    layer: FeatureLayer,
    setLighthouseData: ((value: LighthouseData) => void) | undefined,
    setSelectedFeature: ((feature: Graphic) => void) | null | undefined,
    setLoading: ((value: boolean) => void) | undefined,
    openLightHouseEditor: () => void,
    feature?: Graphic,
    shouldHideLighthouses: boolean = true
) => {
    const view = getMapView();
    if (!view?.map) return;

    setLoading?.(true);

    const sectorTable = view.map.findTableById("Lyssektor") as FeatureLayer;
    const lightLayer = view.map.findLayerById("Lys") as FeatureLayer;

    const read = await readSectorsForFeature(objectid, layer);

    if (read.kind === "no-related-table") {
        if (!feature) {
            setLoading?.(false);
            return;
        }
        const lighthouseData = {
            id: feature?.attributes.globalid ?? uuidv4(),
            center: feature?.geometry as Point,
            sectors: createSingleLightSector(feature),
            setState: setLighthouseData,
        };

        setLighthouseData?.(lighthouseData);

        // Giving a light its first sectors is an edit of the light
        setNewStatusToFeature(feature, NFSStatus.PROJECTED_EXISTING);

        const trueNorth = await computeTrueNorth(feature?.geometry as Point);
        await saveLighthouse(
            lighthouseData,
            setSelectedFeature,
            feature,
            trueNorth,
            () => {},
            () => {}
        );
        openLightHouseEditor();
        setLoading?.(false);
        return;
    }

    // "not-related-to-object": sectors exist on the relationship, but none belong to this object.
    // "no-map-view": the map view wasn't ready when the read ran. Both are silent bails — nothing to write.
    if (read.kind === "not-related-to-object" || read.kind === "no-map-view") {
        setLoading?.(false);
        return;
    }

    const fknavinst = read.lighthouseId;

    // Find relationship IDs
    const sectorTableToLightRelation = getRelationshipId(lightLayer);

    // Get the lighthouse geometry (we need this to get the center of the lighthouse)
    const lighthouseObject = await sectorTable.queryRelatedFeatures({
        outFields: ["*"],
        relationshipId: sectorTableToLightRelation,
        objectIds: [read.sectorObjectId],
        returnGeometry: true,
        geometryPrecision: 20,
    });

    const lighthouseFeatures = lighthouseObject[read.sectorObjectId]?.features;

    const lighthouseGeometry =
        lighthouseFeatures && lighthouseFeatures.length > 0 ? lighthouseFeatures[0].geometry : undefined;

    if (!lighthouseGeometry || lighthouseGeometry.type !== "point") {
        setLoading?.(false);
        return;
    }

    const lighthouseData = {
        id: fknavinst,
        center: new Point({
            x: lighthouseGeometry.x,
            y: lighthouseGeometry.y,
            spatialReference: view.spatialReference,
        }),
        sectors: read.sectors,
        setState: setLighthouseData,
    };

    // Update the lighthouse data object with the new sectors
    const lighthouseFeature = lighthouseObject[read.sectorObjectId]?.features[0];
    if (!lighthouseFeature) {
        setLoading?.(false);
        return;
    }

    lighthouseFeature.attributes.lighthouseData = lighthouseData;
    lighthouseFeature.layer = lightLayer;

    // Set the selected feature and lighthouse data in the state
    // setSelectedFeature?.(lighthouseFeature);
    setLighthouseData?.(lighthouseData);
    openLightHouseEditor();
    setLoading?.(false);

    if (shouldHideLighthouses) hideLighthouses([lighthouseFeature], false);

    return lighthouseData;
};

export const saveLighthouse = async (
    lighthouseData: LighthouseData,
    setSelectedFeature: ((feature: Graphic) => void) | null | undefined,
    selectedFeature: Graphic,
    trueNorth: number,
    setIsSavingLighthouse: (isSaving: boolean) => void,
    setLoading: (isLoading: boolean) => void,
    lighthouseEditingLayer: GraphicsLayer | null = null
) => {
    const mapView = getMapView();
    if (!mapView?.map) return;

    if (!selectedFeature) return;

    setIsSavingLighthouse(true);
    setLoading(true);

    const sectorTable = mapView.map.findTableById("Lyssektor") as FeatureLayer;
    const sectorLayer = mapView.map.findLayerById("Sektorflate") as FeatureLayer;
    const lineLayer = mapView.map.findLayerById("Sektorlinje") as FeatureLayer;
    lighthouseEditingLayer ??= mapView.map.findLayerById("FyrlyktEditing") as GraphicsLayer;
    const lighthouseOverlayLayer = mapView.map.findLayerById("lighthouseOverlayLayer") as GraphicsLayer;
    const lightLayer = mapView.map.findLayerById("Lys") as FeatureLayer;

    const existingSectors = await sectorTable.queryFeatures({
        where: `fknavinst = '${lighthouseData.id}'`,
        outFields: ["*"],
        returnGeometry: true,
        geometryPrecision: 20,
    });

    const existingLights = await lightLayer.queryFeatures({
        where: `globalid = '${lighthouseData.id}'`,
        outFields: ["*"],
        returnGeometry: true,
        geometryPrecision: 20,
    });

    const sectorText = {
        1: "W",
        3: "R",
        4: "G",
        0: "D", // Include this to make Typescript happy
    };

    let lightGlobalId: string | null | undefined = "";
    let lightObjectId = null;
    let lys = null;

    await drawSectors({
        graphicsLayer: lighthouseEditingLayer,
        data: lighthouseData,
        setIsEditing: () => {},
        sectorsToFill: [],
        opacity: 1,
        trueNorthCorrection: trueNorth,
        overrideSectorWidth: true,
        centerFeature: selectedFeature,
    });

    await lighthouseEditingLayer.load();
    const lightGeometry = lighthouseEditingLayer.graphics.find(
        (graphic) => graphic.attributes.lighthouseId === lighthouseData.id && graphic.attributes.objectType === "center"
    )?.geometry;

    selectedFeature.geometry = lightGeometry;

    // Save light object
    if (existingLights.features.length === 0) {
        const region = await getKystverketRegion(selectedFeature);
        const rode = await getKystverketRode(selectedFeature);
        if (region) {
            selectedFeature.attributes.kystverk_region = region;
        }
        if (rode) {
            selectedFeature.attributes.kystverk_rode = rode;
        }
        const editsResult = await applyEdits(
            lightLayer,
            {
                addFeatures: [selectedFeature],
            },
            {},
            setLoading
        );
        lightGlobalId = editsResult.addFeatureResults[0].globalId;
        lightObjectId = editsResult.addFeatureResults[0].objectId;
        setFeatureAttribute(selectedFeature, "objectid", lightObjectId);
        setFeatureAttribute(selectedFeature, "globalid", lightGlobalId);
    } else {
        const editsResult = await applyEdits(
            lightLayer,
            {
                updateFeatures: [selectedFeature],
            },
            {},
            setLoading
        );
        lightGlobalId = editsResult.updateFeatureResults[0].globalId;
        lightObjectId = editsResult.updateFeatureResults[0].objectId;
    }

    const tableSectorsToAdd = [];
    const sectorGeometriesToAdd = [];
    const lineGeometriesToAdd = [];
    const tableSectorsToUpdate = [];
    const sectorGeometriesToUpdate = [];
    const lineGeometriesToUpdate = [];
    const tableSectorsToDelete = [];
    const sectorGeometriesToDelete = [];
    const lineGeometriesToDelete = [];

    const sectorNumberByGlobalId = new Map<string, number>();
    let displaySectorNumber = 1;
    for (const sector of shiftSectors(lighthouseData)) {
        if (sector.color === SectorColor.DARK) continue;
        sectorNumberByGlobalId.set(sector.globalid, displaySectorNumber);
        displaySectorNumber++;
    }

    // A lighthouse is one navigation object, so every sector, sector area and sector line takes the
    // light's status. Callers mark the light before saving (e.g. Prosjektert/Eksisterende on edit).
    // Lyssektor has no columns for nfs_status_line1/2, so line statuses cannot be tracked per line.
    const lighthouseStatus: NFSStatus = selectedFeature.attributes.nfs_status ?? NFSStatus.PROJECTED;

    // Remove dark sectors from the lighthouse data. These shall not be published to the database.
    const lighthouseDataWithoutDarkSectors = lighthouseData.sectors.filter(
        (sector) => sector.color !== SectorColor.DARK
    );
    for (const [index, sector] of lighthouseDataWithoutDarkSectors.entries()) {
        sector.index = index;
        sector.nfs_status = lighthouseStatus;
        sector.nfs_status_line1 = lighthouseStatus;
        sector.nfs_status_line2 = lighthouseStatus;
    }

    for (const sector of existingSectors.features) {
        const lighthouseDataSector = lighthouseDataWithoutDarkSectors.find(
            (s) => s.globalid === sector.attributes.globalid
        );

        if (lighthouseDataSector) {
            // Find geometry for corresponding sectors in the graphics layer
            await lighthouseEditingLayer.load();
            const sectorGeometryMap = lighthouseEditingLayer.graphics.find(
                (graphic) =>
                    graphic.attributes.globalid === sector.attributes.globalid &&
                    graphic.attributes.objectType === "sector"
            );

            const currentOriginalSectorIndex = lighthouseData.sectors.findIndex(
                (s) => s.globalid === lighthouseDataSector.globalid
            );
            const nextSector = lighthouseData.sectors[(currentOriginalSectorIndex + 1) % lighthouseData.sectors.length];

            // Set new attributes from lighthouseData
            sector.attributes.lys_farge = Number.parseInt(lighthouseDataSector.color?.toString() ?? "1");
            sector.attributes.retning_sektorlinje_1 = lighthouseDataSector.startAngle;
            sector.attributes.retning_sektorlinje_2 = nextSector.startAngle;
            sector.attributes.sektor_radius =
                (lighthouseDataSector.innerRadius ?? DefaultSectorValues.defaultInnerRadius) / metersPerNauticalMile;
            sector.attributes.lengde_sektorlinje_1 =
                (lighthouseDataSector.startLineLength ?? DefaultSectorValues.defaultLineLength) / metersPerNauticalMile;
            sector.attributes.lengde_sektorlinje_2 =
                (nextSector.startLineLength ?? DefaultSectorValues.defaultLineLength) / metersPerNauticalMile;
            sector.attributes.beskrivelse = lighthouseDataSector.description;
            sector.attributes.sektor_tekst = sectorText[lighthouseDataSector.color!];
            sector.attributes.sektor_nr =
                sectorNumberByGlobalId.get(lighthouseDataSector.globalid) ?? lighthouseDataSector.index + 1;
            sector.attributes.nfs_status = lighthouseStatus;

            tableSectorsToUpdate.push(sector);

            //////////

            // Find data from database for corresponding sectors
            const sectorDataDBRelation = await sectorTable.queryRelatedFeatures({
                outFields: ["*"],
                relationshipId: getRelationshipId(sectorLayer),
                objectIds: [sector.attributes.objectid],
                returnGeometry: true,
                geometryPrecision: 20,
            });

            const sectorGeometry = await sectorLayer.queryFeatures({
                where: `fksektorid = '${sector.attributes.globalid}'`,
                outFields: ["*"],
                returnGeometry: true,
            });

            if (sectorGeometry.features.length === 0) {
                const sectorGeometryDB = new Graphic({
                    attributes: {
                        fksektorid: sectorGeometryMap?.attributes.globalid,
                        lys_farge: sectorGeometryMap?.attributes.lys_farge,
                        nfs_status: lighthouseStatus,
                    },
                    geometry: sectorGeometryMap?.geometry,
                });

                sectorGeometriesToAdd.push(sectorGeometryDB);

                const lineGraphic1 = lighthouseEditingLayer.graphics.find(
                    (graphic) =>
                        graphic.attributes.objectType === "line" &&
                        graphic.attributes.sectorId === sectorGeometryMap?.attributes.globalid
                );

                const lineGraphic2 = lighthouseEditingLayer.graphics.find(
                    (graphic) =>
                        graphic.attributes.objectType === "line" &&
                        graphic.attributes.index ===
                            (lineGraphic1?.attributes.index + 1) % lighthouseData.sectors.length
                );

                const newLine1 = new Graphic({
                    geometry: lineGraphic1?.geometry,
                    attributes: {
                        fksektorid: sectorGeometryMap?.attributes.globalid,
                        nfs_status: lighthouseStatus,
                    },
                });

                const newLine2 = new Graphic({
                    geometry: lineGraphic2?.geometry,
                    attributes: {
                        fksektorid: sectorGeometryMap?.attributes.globalid,
                        nfs_status: lighthouseStatus,
                    },
                });

                lineGeometriesToAdd.push(newLine1, newLine2);
            }

            if (Object.keys(sectorDataDBRelation).length > 0) {
                const sectorGeometryDBFeature = sectorDataDBRelation[sector.attributes.objectid]?.features[0];
                if (!sectorGeometryDBFeature) {
                    toast.error("Feil ved lagring av fyrlyktsektor: Kunne ikke finne sektor geometri i databasen.");
                    continue;
                }

                // Apply edits done in the graphics layer to the database
                sectorGeometryDBFeature.geometry = sectorGeometryMap?.geometry;
                sectorGeometryDBFeature.attributes.lys_farge = Number.parseInt(
                    lighthouseDataSector.color?.toString() ?? SectorColor.WHITE
                );
                sectorGeometryDBFeature.attributes.nfs_status = lighthouseStatus;
                sectorGeometriesToUpdate.push(sectorGeometryDBFeature);
            }

            //////////

            // Find geometry, in the graphics layer, for the one sector line corresponding to the sector
            const lineGraphic1 = lighthouseEditingLayer.graphics.find(
                (graphic) =>
                    graphic.attributes.objectType === "line" &&
                    graphic.attributes.sectorId === sector.attributes.globalid
            );

            // Find geometry for the line corresponding to the NEXT sector. This is equivalent to the second sector line of the current sector
            const lineGraphic2 = lighthouseEditingLayer.graphics.find(
                (graphic) =>
                    graphic.attributes.objectType === "line" &&
                    graphic.attributes.index === (lineGraphic1?.attributes.index + 1) % lighthouseData.sectors.length
            );

            // Find data from database for corresponding sector lines
            const sectorLines = await sectorTable.queryRelatedFeatures({
                outFields: ["*"],
                relationshipId: getRelationshipId(lineLayer),
                objectIds: [sector.attributes.objectid],
            });

            if (Object.keys(sectorLines).length === 0) {
                if (lineGraphic1) {
                    lineGraphic1.attributes.nfs_status = lighthouseStatus;
                }
                if (lineGraphic2) {
                    lineGraphic2.attributes.nfs_status = lighthouseStatus;
                }
                lineGeometriesToAdd.push(lineGraphic1, lineGraphic2);
            } else {
                // Apply edits done in the graphics layer to the database
                const sectorLineFeatures = sectorLines[sector.attributes.objectid]?.features;
                if (!sectorLineFeatures) {
                    toast.error("Feil ved lagring av fyrlyktsektor: Kunne ikke finne sektor geometri i databasen.");
                    continue;
                }
                sectorLineFeatures[0].geometry = lineGraphic1?.geometry;
                sectorLineFeatures[1].geometry = lineGraphic2?.geometry;
                sectorLineFeatures[0].attributes.nfs_status = lighthouseStatus;
                sectorLineFeatures[1].attributes.nfs_status = lighthouseStatus;
                lineGeometriesToUpdate.push(...sectorLineFeatures);
            }
        } else {
            const sectorLines = await sectorTable.queryRelatedFeatures({
                outFields: ["*"],
                relationshipId: getRelationshipId(lineLayer),
                objectIds: [sector.attributes.objectid],
            });
            const sectorGraphicDB = await sectorTable.queryRelatedFeatures({
                outFields: ["*"],
                relationshipId: getRelationshipId(sectorLayer),
                objectIds: [sector.attributes.objectid],
            });

            sector.attributes.nfs_status = NFSStatus.PROJECTED_DISCONTINUED;
            const sectorGeometries = sectorGraphicDB[sector.attributes.objectid]?.features;
            for (const geometry of sectorGeometries ?? []) {
                geometry.attributes.nfs_status = NFSStatus.PROJECTED_DISCONTINUED;
            }
            const lineGeometries = sectorLines[sector.attributes.objectid]?.features;
            for (const geometry of lineGeometries ?? []) {
                geometry.attributes.nfs_status = NFSStatus.PROJECTED_DISCONTINUED;
            }

            tableSectorsToDelete.push(sector);
            if (sectorGeometries) {
                sectorGeometriesToDelete.push(...sectorGeometries);
            }
            if (lineGeometries) {
                lineGeometriesToDelete.push(...lineGeometries);
            }
        }
    }

    for (const [index, sector] of lighthouseDataWithoutDarkSectors.entries()) {
        const existingSector = existingSectors.features.find((s) => s.attributes.globalid === sector.globalid);

        const currentSectorIndexOfAll = lighthouseData.sectors.findIndex((s) => s.globalid === sector.globalid);
        const nextIndex = (currentSectorIndexOfAll + 1) % lighthouseData.sectors.length;

        if (!existingSector) {
            const newSector = new Graphic({
                attributes: {
                    globalid: sector.globalid,
                    fknavinst: lightGlobalId,
                    lys_farge: Number.parseInt(sector.color?.toString() ?? "1"),
                    retning_sektorlinje_1: sector.startAngle,
                    retning_sektorlinje_2: lighthouseData.sectors[nextIndex].startAngle,
                    sektor_radius:
                        (sector.innerRadius ?? DefaultSectorValues.defaultInnerRadius) / metersPerNauticalMile,
                    lengde_sektorlinje_1:
                        (sector.startLineLength ?? DefaultSectorValues.defaultLineLength) / metersPerNauticalMile,
                    lengde_sektorlinje_2:
                        (lighthouseData.sectors[nextIndex].startLineLength ?? DefaultSectorValues.defaultLineLength) /
                        metersPerNauticalMile,
                    beskrivelse: sector.description,
                    sektor_tekst: sectorText[sector.color!],
                    lys: lys,
                    sektor_nr: sectorNumberByGlobalId.get(sector.globalid) ?? index + 1,
                    nfs_status: lighthouseStatus,
                },
            });

            tableSectorsToAdd.push(newSector);

            await lighthouseEditingLayer.load();
            const sectorGeometryMap = lighthouseEditingLayer.graphics.find(
                (graphic) => graphic.attributes.globalid === sector.globalid
            );

            const sectorGeometryDB = new Graphic({
                attributes: {
                    fksektorid: sector.globalid,
                    lys_farge: sector.color,
                    nfs_status: lighthouseStatus,
                },
                geometry: sectorGeometryMap?.geometry,
            });

            sectorGeometriesToAdd.push(sectorGeometryDB);

            const lineGraphic1 = lighthouseEditingLayer.graphics.find(
                (graphic) => graphic.attributes.objectType === "line" && graphic.attributes.sectorId === sector.globalid
            );

            const lineGraphic2 = lighthouseEditingLayer.graphics.find(
                (graphic) =>
                    graphic.attributes.objectType === "line" &&
                    graphic.attributes.index === (lineGraphic1?.attributes.index + 1) % lighthouseData.sectors.length
            );

            const newLine1 = new Graphic({
                geometry: lineGraphic1?.geometry,
                attributes: {
                    fksektorid: sector.globalid,
                    nfs_status: lighthouseStatus,
                },
            });

            const newLine2 = new Graphic({
                geometry: lineGraphic2?.geometry,
                attributes: {
                    fksektorid: sector.globalid,
                    nfs_status: lighthouseStatus,
                },
            });

            lineGeometriesToAdd.push(newLine1, newLine2);
        }
    }

    // Apply all edits to the database
    await applyEdits(
        sectorTable,
        {
            addFeatures: tableSectorsToAdd,
            updateFeatures: tableSectorsToUpdate,
            deleteFeatures: tableSectorsToDelete,
        },
        { globalIdUsed: true },
        setLoading
    );

    await applyEdits(
        sectorLayer,
        {
            addFeatures: sectorGeometriesToAdd.filter((f) => f !== undefined),
            updateFeatures: sectorGeometriesToUpdate.filter((f) => f !== undefined),
            deleteFeatures: sectorGeometriesToDelete.filter((f) => f !== undefined),
        },
        {},
        setLoading
    );

    await applyEdits(
        lineLayer,
        {
            addFeatures: lineGeometriesToAdd.filter((f) => f !== undefined),
            updateFeatures: lineGeometriesToUpdate.filter((f) => f !== undefined),
            deleteFeatures: lineGeometriesToDelete.filter((f) => f !== undefined),
        },
        {},
        setLoading
    );

    setIsSavingLighthouse(false);
    setLoading(false);

    lighthouseEditingLayer.removeAll();
    lighthouseOverlayLayer.removeAll();

    findAndSelectFeature(selectedFeature.layer as FeatureLayer, selectedFeature, (graphic) => {
        setSelectedFeature?.(graphic);
    });

    return lightObjectId;
};

export interface CreateLighthouseAtPointOptions {
    /** The preset feature for the picked object type. Cloned, never mutated. */
    baseFeature: Graphic;
    /** Where the lighthouse goes - the point the user placed on the map. */
    center: Point;
    isFyrstasjon: boolean;
    /** Replacing an existing object: the caller keeps ownership of the related features. */
    isReplacing?: boolean;
    /** Sectors to create the lighthouse with. Defaults to the configured default sectors. */
    sectors?: Sector[];
    userName: string | null | undefined;
    /** Title of the picked AppConfig.CreateObject entry, used to find its related layers. */
    entryTitle: string | null | undefined;
    setSelectedFeature: ((feature: Graphic) => void) | null | undefined;
    /** Also stored as the lighthouse data's own setState, as loadLighthouse does. */
    setLighthouseData: (lighthouseData: LighthouseData) => void;
    setTrueNorth: (trueNorth: number) => void;
    setIsSavingLighthouse: (isSaving: boolean) => void;
    setLoading: (isLoading: boolean) => void;
}

/**
 * Creates and saves a new lighthouse at the given point. Placement is a parameter, so the
 * caller decides where it goes - the create-object tool passes the point the user sketched,
 * the replace flow passes the geometry of the object being replaced.
 */
export const createLighthouseAtPoint = async (options: CreateLighthouseAtPointOptions) => {
    const mapView = getMapView();
    if (!mapView?.map) return null;

    // Anything left from a previous lighthouse edit must not bleed into the new one
    const lighthouseEditingLayer = mapView.map.findLayerById("FyrlyktEditing") as GraphicsLayer;
    lighthouseEditingLayer?.removeAll();

    const newFeature = options.baseFeature.clone();

    fillLighthouseAttributes(newFeature);

    const lighthouseData = buildLighthouseData(newFeature, {
        center: options.center,
        isFyrstasjon: options.isFyrstasjon,
        sectors: options.sectors,
        setState: options.setLighthouseData,
    });

    options.setSelectedFeature?.(newFeature);
    options.setLighthouseData(lighthouseData);

    const trueNorth = await computeTrueNorth(lighthouseData.center);
    options.setTrueNorth(trueNorth);

    const objectid = await saveLighthouse(
        lighthouseData,
        options.setSelectedFeature,
        newFeature,
        trueNorth,
        options.setIsSavingLighthouse,
        options.setLoading
    );

    if (objectid === undefined || objectid === null || options.isReplacing) {
        return { newFeature, lighthouseData, objectid };
    }

    const lightLayer = mapView.map.findLayerById("Lys") as FeatureLayer;
    const selectedLayerConfig = AppConfig.CreateObject.flatMap((group) => group.GroupEntries).find(
        (entry) => entry.Title === options.entryTitle
    );

    createRelatedFeatures(newFeature, lightLayer, selectedLayerConfig, options.userName, false);

    return { newFeature, lighthouseData, objectid };
};

export const hasSectors = async (feature: Graphic) => {
    const related = await getAllRelatedFeatures(feature);
    return related.some((r) => r.relationship.name === "Lyssektor");
};

export const hasSectorGeometry = async (feature: Graphic) => {
    const mapView = getMapView();
    if (!mapView) return false;

    const related = await getAllRelatedFeatures(feature);
    const sectorRelationship = related.find((r) => r.relationship.name === "Lyssektor");
    const relatedSectors = sectorRelationship?.features.map(async (f) => {
        f.layer = mapView.map?.findTableById("Lyssektor");
        return await getAllRelatedFeatures(f);
    });
    const allResolved = await Promise.all(relatedSectors ?? []);
    const allSectorGeometries = allResolved.flatMap(
        (s) => s.find((r) => r.relationship.name === "Lyssektor_F")?.features
    );
    return allSectorGeometries.some(Boolean);
};

export const isLighthouse = (feature: Graphic | null | undefined) => {
    if (!feature) return false;
    return [NauticalClasses.FL_Fyrlykt, NauticalClasses.FS_Fyrstasjon].includes(feature.attributes.klasse);
};

export const canHaveSectors = (feature: Graphic) => {
    return feature.layer?.title === "Lys";
};

export const hideLighthouses = async (
    features: (Graphic | null | undefined)[] | null | undefined,
    showRemovedFeatures: boolean
) => {
    // Hide all static sectors and lines - we are drawing our own graphics that can be edited
    const view = getMapView();
    if (!view?.map) return;

    const sectorTable = view.map.findTableById("Lyssektor") as FeatureLayer;
    const sectorLayer = view.map.findLayerById("Sektorflate") as FeatureLayer;
    const lineLayer = view.map.findLayerById("Sektorlinje") as FeatureLayer;
    const lightLayer = view.map.findLayerById("Lys") as FeatureLayer;
    sectorLayer.visible = true;
    lineLayer.visible = true;
    lightLayer.visible = true;

    const sectorTableToLightRelation = getRelationshipId(lightLayer);
    const sectorTableToSectorGeometryRelation = getRelationshipId(sectorLayer);
    const sectorTableToLineGeometryRelation = getRelationshipId(lineLayer);

    const objectids = features?.map((feature) => feature?.attributes.objectid) ?? [];

    const allSectorDatas = await lightLayer.queryRelatedFeatures({
        outFields: ["*"],
        relationshipId: sectorTableToLightRelation,
        objectIds: objectids,
    });

    const allSectorFeatures = objectids.flatMap((objectid) => {
        return allSectorDatas[objectid]?.features ?? [];
    });

    if (!allSectorFeatures || allSectorFeatures.length === 0) return;

    const allSectorGeometries = (await sectorTable.queryRelatedFeatures({
        outFields: ["*"],
        relationshipId: sectorTableToSectorGeometryRelation,
        objectIds: allSectorFeatures.map((graphic: Graphic) => graphic.attributes.objectid),
    })) as object;

    const allSectorObjectIds = Object.values(allSectorGeometries).map(
        (sectorGeometries) => sectorGeometries.features[0].attributes.objectid
    );

    const allLineGeometries = (await sectorTable.queryRelatedFeatures({
        outFields: ["*"],
        relationshipId: sectorTableToLineGeometryRelation,
        objectIds: allSectorFeatures.map((graphic: Graphic) => graphic.attributes.objectid),
    })) as object;

    const allLineObjectIds = Object.values(allLineGeometries).map((lineGeometries) =>
        lineGeometries.features.flatMap((feature: Graphic) => feature.attributes.objectid)
    );

    sectorLayer.definitionExpression =
        removedAndDiscontinuedDefExpr(sectorLayer, showRemovedFeatures) +
        ` AND objectid not in (${allSectorObjectIds.join(",")})`;
    lineLayer.definitionExpression =
        removedAndDiscontinuedDefExpr(lineLayer, showRemovedFeatures) +
        ` AND objectid not in (${allLineObjectIds.join(",")})`;
};

export const unhideLighthouses = (showRemovedFeatures: boolean) => {
    const view = getMapView();
    if (!view?.map) return;

    const sectorLayer = view.map.findLayerById("Sektorflate") as FeatureLayer;
    const lineLayer = view.map.findLayerById("Sektorlinje") as FeatureLayer;
    const lightLayer = view.map.findLayerById("Lys") as FeatureLayer;

    sectorLayer.definitionExpression = removedAndDiscontinuedDefExpr(sectorLayer, showRemovedFeatures);
    lineLayer.definitionExpression = removedAndDiscontinuedDefExpr(lineLayer, showRemovedFeatures);
    lightLayer.definitionExpression = removedAndDiscontinuedDefExpr(lightLayer, showRemovedFeatures);
};

export const removeLighthouseEditingOverlay = () => {
    const mapView = getMapView();
    if (!mapView?.map) return;
    const lighthouseOverlayLayer = mapView.map.findLayerById("lighthouseOverlayLayer") as GraphicsLayer;
    if (!lighthouseOverlayLayer) return;
    lighthouseOverlayLayer.removeAll();
};
